import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import {
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  CalendarIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [showDuplicatesBanner, setShowDuplicatesBanner] = useState(true);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [selectedDuplicateIndex, setSelectedDuplicateIndex] = useState(0);
  const { user } = useAuth();
  const { organismeActif, isSuperAdminMode } = useOrganisme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || (!['admin', 'super_admin'].includes(user.role) && user.role !== 'super_admin')) {
      navigate('/');
      return;
    }

    fetchStatistiques();
    fetchDuplicates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, organismeActif, isSuperAdminMode]);

  useEffect(() => {
    if (selectedYear !== null) {
      fetchStatistiques(selectedYear);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchStatistiques = async (annee = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = annee 
        ? `${process.env.REACT_APP_API_URL}/settings/statistiques?annee=${annee}`
        : `${process.env.REACT_APP_API_URL}/settings/statistiques`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      if (selectedYear === null && response.data.anneeActuelle) {
        setSelectedYear(response.data.anneeActuelle);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDuplicates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users-management/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('results', response.data);
      setDuplicates(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des doublons:', error);
    }
  };

  const getTotalStats = (data) => {
    if (!data || !Array.isArray(data)) return { total: 0, actifs: 0, enCours: 0, attentePaiement: 0, abandonnees: 0 };
    return data.reduce((acc, org) => ({
      total: acc.total + (org.total || 0),
      actifs: acc.actifs + (org.actifs || 0),
      enCours: acc.enCours + (org.enCours || 0),
      attentePaiement: acc.attentePaiement + (org.attentePaiement || 0),
      abandonnees: acc.abandonnees + (org.abandonnees || 0)
    }), { total: 0, actifs: 0, enCours: 0, attentePaiement: 0, abandonnees: 0 });
  };

  if (loading && !stats) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const anneeActuelle = stats?.anneeActuelle || new Date().getFullYear();
  const anneesDisponibles = stats?.anneesDisponibles || [anneeActuelle];
  
  // En mode Super Admin, afficher tous les organismes
  // En mode Admin Organisme, afficher uniquement l'organisme actif
  const currentStats = isSuperAdminMode 
    ? getTotalStats(stats?.stats) 
    : getTotalStats(stats?.stats?.filter(s => s._id === organismeActif));
  
  // Stats détaillées : tous les organismes en mode Super Admin, sinon uniquement l'organisme actif
  const statsParOrganisme = isSuperAdminMode 
    ? stats?.stats 
    : (organismeActif ? stats?.stats?.filter(s => s._id === organismeActif) : null);

  return (
    <div className="h-full bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center text-sm text-gray-500">
          <span className="font-medium text-gray-900">Abeille Réunion</span>
          <svg className="mx-2 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span>Dashboard</span>
        </div>
      </div>

      {/* Header */}
      <div className="px-8 py-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Administrateur</h1>
            <p className="mt-2 text-sm text-gray-600">
              {isSuperAdminMode 
                ? "Vue d'ensemble de tous les organismes"
                : `Vue d'ensemble des adhésions pour ${organismeActif || 'l\'organisme sélectionné'}`
              }
            </p>
          </div>
          {!isSuperAdminMode && (
            <HelpButton 
              title={helpContent.dashboard.title}
              sections={helpContent.dashboard.sections}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-8 pb-8 space-y-6">
        {/* Bandeau d'alerte doublons */}
        {duplicates && duplicates.total > 0 && showDuplicatesBanner && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-sm font-bold text-red-800">
                  ⚠️ Attention : {duplicates.total} doublon{duplicates.total > 1 ? 's' : ''} détecté{duplicates.total > 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setShowDuplicatesModal(true)}
                  className="ml-4 px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                >
                  Voir
                </button>
              </div>
              <button
                onClick={() => setShowDuplicatesBanner(false)}
                className="text-red-400 hover:text-red-600 ml-4"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Modale des doublons */}
        {showDuplicatesModal && duplicates && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Doublons détectés ({duplicates.total})
                  </h2>
                  <button
                    onClick={() => setShowDuplicatesModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Menu déroulant pour sélectionner un doublon */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sélectionner un doublon à examiner :
                  </label>
                  <select
                    value={selectedDuplicateIndex}
                    onChange={(e) => setSelectedDuplicateIndex(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {duplicates.duplicates.map((dup, index) => (
                      <option key={index} value={index}>
                        {dup.type}: {dup.value} ({dup.count} utilisateurs)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Affichage des utilisateurs côte à côte */}
                {duplicates.duplicates[selectedDuplicateIndex] && (
                  <div>
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm font-medium text-red-800">
                        <strong>Type de doublon :</strong> {duplicates.duplicates[selectedDuplicateIndex].type}
                      </p>
                      <p className="text-sm text-red-700">
                        <strong>Valeur :</strong> {duplicates.duplicates[selectedDuplicateIndex].value}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {duplicates.duplicates[selectedDuplicateIndex].users.map((user, index) => (
                        <div key={index} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              Utilisateur {index + 1}
                            </h3>
                            <UsersIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Nom :</span>
                              <span className="ml-2 text-gray-900">{user.nom || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Prénom :</span>
                              <span className="ml-2 text-gray-900">{user.prenom || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Email :</span>
                              <span className="ml-2 text-gray-900">{user.email || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">NAPI :</span>
                              <span className="ml-2 text-gray-900">{user.napi || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">SIRET :</span>
                              <span className="ml-2 text-gray-900">{user.siret || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">AMEXA :</span>
                              <span className="ml-2 text-gray-900">{user.numeroAmexa || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-700">Adhésions :</span>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {user.adhesions && user.adhesions.length > 0 ? (
                                  user.adhesions.map((adhesion, idx) => {
                                    const bgColor = adhesion.organisme === 'ADAR' ? 'bg-blue-50' : 
                                                    adhesion.organisme === 'SAR' ? 'bg-green-50' : 
                                                    adhesion.organisme === 'AMAIR' ? 'bg-pink-50' : 'bg-yellow-50';
                                    const textColor = adhesion.organisme === 'ADAR' ? 'text-[#0B4395]' : 
                                                      adhesion.organisme === 'SAR' ? 'text-green-700' : 
                                                      adhesion.organisme === 'AMAIR' ? 'text-pink-700' : 'text-yellow-700';
                                    const statusIcon = adhesion.status === 'actif' ? '✓' : 
                                                       adhesion.status === 'en_attente' ? '⏳' : 
                                                       adhesion.status === 'paiement_demande' ? '💳' : '•';
                                    return (
                                      <span
                                        key={idx}
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${bgColor} ${textColor}`}
                                        title={`${adhesion.organisme} ${adhesion.annee} - ${adhesion.status}`}
                                      >
                                        {statusIcon} {adhesion.organisme} {adhesion.annee}
                                      </span>
                                    );
                                  })
                                ) : (
                                  <span className="text-gray-500 text-xs">Aucune adhésion</span>
                                )}
                              </div>
                            </div>
                            <div className="pt-2 border-t border-gray-200">
                              <span className="font-medium text-gray-700">ID :</span>
                              <span className="ml-2 text-gray-600 text-xs font-mono">{user._id}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end">
                <button
                  onClick={() => setShowDuplicatesModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Statut adhésions année suivante */}
        {stats?.adhesionsSuivantesOuvertes && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800">
                Les adhésions pour l'année {anneeActuelle + 1} sont ouvertes
              </span>
            </div>
          </div>
        )}

        {/* Statistiques de l'année sélectionnée */}
        <div>
          <div className="flex items-center justify-start gap-4 mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <CalendarIcon className="h-6 w-6 mr-2 text-indigo-600" />
              Statistiques
              {loading && <span className="ml-2 text-sm text-gray-500">(chargement...)</span>}
            </h2>
            
            {/* Filtre par année */}
            <div className="flex flex-wrap gap-2">
              {anneesDisponibles.map((annee) => (
                <button
                  key={annee}
                  onClick={() => setSelectedYear(annee)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedYear === annee
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {annee}
                  {annee === anneeActuelle && (
                    <span className="ml-1 text-xs opacity-75">(en cours)</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Stats globales ou par organisme */}
          {isSuperAdminMode ? (
            // Mode Super Admin: afficher les stats de tous les organismes
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
              {statsParOrganisme?.map((orgStats) => (
                <div key={orgStats._id} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className={`px-6 py-4 bg-gradient-to-r ${
                    orgStats._id === 'SAR' ? 'from-green-500 to-green-600' : 
                    orgStats._id === 'ADAR' ? 'from-[#0B4395] to-[#0d5ac4]' : 
                    'from-honey-500 to-honey-600'
                  }`}>
                    <h3 className="text-lg font-semibold text-white">{orgStats._id}</h3>
                    <p className={`text-sm ${
                      orgStats._id === 'SAR' ? 'text-green-100' : 
                      orgStats._id === 'ADAR' ? 'text-blue-100' : 
                      'text-honey-100'
                    }`}>
                      {orgStats._id === 'SAR' ? 'Syndicat Apicole de la Réunion' : 
                       orgStats._id === 'AMAIR' ? 'Association pour le Développement de l\'Apiculture à la Réunion' :
                       orgStats._id === 'ADAR' ? 'Association pour le Développement de l\'Apiculture à la Réunion' :
                       ''}
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <UsersIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Total</span>
                        </div>
                        <span className="text-xl font-bold text-gray-900">{orgStats.total || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-sm text-gray-600">Actifs</span>
                        </div>
                        <span className="text-lg font-semibold text-green-600">{orgStats.actifs || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <ClockIcon className="h-5 w-5 text-yellow-500 mr-2" />
                          <span className="text-sm text-gray-600">En cours</span>
                        </div>
                        <span className="text-lg font-semibold text-yellow-600">{orgStats.enCours || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CreditCardIcon className="h-5 w-5 text-honey-500 mr-2" />
                          <span className="text-sm text-gray-600">En attente de paiement</span>
                        </div>
                        <span className="text-lg font-semibold text-honey-600">{orgStats.attentePaiement || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                          <span className="text-sm text-gray-600">Abandons</span>
                        </div>
                        <span className="text-lg font-semibold text-red-600">{orgStats.abandonnees || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Mode Admin Organisme: afficher les stats de l'organisme actif uniquement
            <div className="bg-white rounded-lg shadow overflow-hidden max-w-2xl">
              <div className={`px-6 py-4 bg-gradient-to-r ${
                organismeActif === 'SAR' ? 'from-green-500 to-green-600' : 
                organismeActif === 'ADAR' ? 'from-[#0B4395] to-[#0d5ac4]' : 
                'from-honey-500 to-honey-600'
              }`}>
                <h3 className="text-lg font-semibold text-white">{organismeActif}</h3>
                <p className={`text-sm ${
                  organismeActif === 'SAR' ? 'text-green-100' : 
                  organismeActif === 'ADAR' ? 'text-blue-100' : 
                  'text-honey-100'
                }`}>
                  {organismeActif === 'SAR' ? 'Syndicat Apicole de la Réunion' : 
                   organismeActif === 'AMAIR' ? 'Association de la Maison de l\'Apiculture de l\'Île de la Réunion' :
                   organismeActif === 'ADAR' ? 'Association pour le Développement de l\'Apiculture à la Réunion' :
                   ''}
                </p>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Total adhérents</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">{currentStats.total}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-sm text-gray-600">Actifs</span>
                    </div>
                    <span className="text-lg font-semibold text-green-600">{currentStats.actifs}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-yellow-500 mr-2" />
                      <span className="text-sm text-gray-600">En cours</span>
                    </div>
                    <span className="text-lg font-semibold text-yellow-600">{currentStats.enCours}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CreditCardIcon className="h-5 w-5 text-honey-500 mr-2" />
                      <span className="text-sm text-gray-600">En attente</span>
                    </div>
                    <span className="text-lg font-semibold text-honey-600">{currentStats.attentePaiement}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      <span className="text-sm text-gray-600">Abandons</span>
                    </div>
                    <span className="text-lg font-semibold text-red-600">{currentStats.abandonnees}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
